import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { getProject, projects } from "@/data/projects";
import { getLenis } from "@/hooks/useSmoothScroll";

const EASE = [0.25, 0.1, 0.25, 1];

function MaskImage({ src, alt, className = "", ratio = "aspect-[16/9]" }) {
  return (
    <div className={`relative overflow-hidden ${ratio} ${className}`}>
      <motion.span
        className="absolute inset-0 z-10 bg-white"
        initial={{ scaleY: 1 }}
        whileInView={{ scaleY: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 1, ease: EASE }}
        style={{ transformOrigin: "top" }}
      />
      <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        initial={{ scale: 1.12 }}
        whileInView={{ scale: 1 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 1.4, ease: EASE }}
        className="h-full w-full object-cover"
      />
    </div>
  );
}

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.9, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const project = getProject(id);

  useEffect(() => {
    const lenis = getLenis();
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [id]);

  if (!project) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <p className="ama-label text-black/50">Project not found</p>
          <button
            data-cursor="link"
            onClick={() => navigate("/")}
            className="mt-6 ama-label text-[#1B3818]"
          >
            ← Back home
          </button>
        </div>
      </div>
    );
  }

  const idx = projects.findIndex((p) => p.id === id);
  const next = projects[(idx + 1) % projects.length];

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6, ease: EASE }}
      className="relative z-10"
      data-testid="project-detail"
    >
      <div className="mx-auto max-w-[1600px] px-6 pt-32 md:px-12 md:pt-40">
        {/* back */}
        <button
          data-cursor="link"
          data-testid="back-button"
          onClick={() => navigate("/#projects")}
          className="ama-label text-black/50 transition-colors duration-300 hover:text-[#1B3818]"
        >
          ← Projects
        </button>

        {/* title block */}
        <div className="mt-12 flex flex-col justify-between gap-8 border-b border-[#e4e4e2] pb-12 md:mt-20 md:flex-row md:items-end">
          <div>
            <span className="ama-label text-[#1B3818]">
              {project.category}
            </span>
            <h1
              className="mt-6 text-5xl font-light leading-[0.95] tracking-tight md:text-8xl"
              data-testid="project-title"
            >
              <Reveal delay={0.15}>{project.title}</Reveal>
            </h1>
          </div>
          <div className="flex gap-12">
            <div>
              <span className="ama-label text-black/40">Location</span>
              <p className="mt-3 text-base font-light">{project.location}</p>
            </div>
            <div>
              <span className="ama-label text-black/40">Year</span>
              <p className="mt-3 text-base font-light mono-num">{project.year}</p>
            </div>
          </div>
        </div>
      </div>

      {/* hero image */}
      <div className="mx-auto mt-12 max-w-[1600px] px-6 md:px-12">
        <MaskImage src={project.cover} alt={project.title} ratio="aspect-[16/9]" />
      </div>

      {/* description */}
      <div className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-36">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-4">
            <p className="text-2xl font-light leading-snug tracking-tight md:text-3xl">
              {project.intro}
            </p>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p className="text-base font-light leading-relaxed text-black/60 md:text-lg">
              {project.description}
            </p>
          </div>
        </div>
      </div>

      {/* gallery: alternating full-width and split */}
      <div className="mx-auto max-w-[1600px] space-y-6 px-6 pb-24 md:px-12 md:pb-36">
        <MaskImage src={project.gallery[0]} alt={`${project.title} 1`} ratio="aspect-[16/9]" />
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <MaskImage src={project.gallery[1]} alt={`${project.title} 2`} ratio="aspect-[4/5]" />
          <MaskImage src={project.gallery[2]} alt={`${project.title} 3`} ratio="aspect-[4/5]" />
        </div>
        <MaskImage src={project.gallery[3]} alt={`${project.title} 4`} ratio="aspect-[16/9]" />
      </div>

      {/* next project */}
      <Link
        to={`/projects/${next.id}`}
        data-cursor="open"
        data-testid="next-project"
        className="group block border-t border-[#e4e4e2]"
      >
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-16 md:px-12 md:py-24">
          <div>
            <span className="ama-label text-black/40">Next Project</span>
            <h3 className="mt-4 text-4xl font-light tracking-tight transition-colors duration-500 group-hover:text-[#1B3818] md:text-7xl">
              {next.title}
            </h3>
            <p className="mt-3 ama-label text-black/45">
              {next.category} — {next.location}
            </p>
          </div>
          <span className="text-3xl transition-transform duration-500 group-hover:translate-x-3">
            →
          </span>
        </div>
      </Link>
    </motion.main>
  );
}
